import { EntityType } from '@frontend/common';
import { FeatureTarget, Functionality, FunctionalityType } from '@frontend/functionality/types';
import { SettingClient } from '@frontend/setting/api';
import { SettingKey } from '@frontend/setting/types';
import { useEffect, useState } from 'react';

import { useMenuRepository } from '../menu/menu-repository';
import { QuickActionsProps } from './quick-actions.component';

const useQuickActions = (props: QuickActionsProps) => {
    const { functionalities } = useMenuRepository();
    const [relevantFunctionalities, setRelevantFunctionalities] = useState<Functionality[] | null>(null);

    useEffect(() => {
        let cancelled = false;

        const resolve = async () => {
            if (!props.application || !functionalities) {
                if (!cancelled) setRelevantFunctionalities([]);
                return;
            }

            const candidates =
                props.application === 'my'
                    ? functionalities.filter((l) => l.type === FunctionalityType.PERSONAL)
                    : functionalities.filter((l) => l.type === FunctionalityType.CORPORATE && l.application === props.application);

            const mobileCandidates = candidates
                .filter((f) => f.hidden !== true && f.target === FeatureTarget.MOBILE)
                .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

            const withAction = await Promise.all(
                mobileCandidates.map(async (functionality) => {
                    if (functionality.wizard_id) return functionality;

                    const settings = await SettingClient.fetchSettings({
                        entity_id: functionality.id,
                        entity_type: EntityType.FUNCTIONALITY
                    });
                    const wizardSetting = settings.results.find((s) => s.key === SettingKey.WIZARD_ID);
                    if (wizardSetting && typeof wizardSetting.value === 'string') return functionality;
                    return null;
                })
            );

            if (!cancelled) {
                setRelevantFunctionalities(withAction.filter((f): f is Functionality => f != null));
            }
        };

        resolve();
        return () => {
            cancelled = true;
        };
    }, [props.application, functionalities]);

    return {
        relevantFunctionalities,
        isLoading: relevantFunctionalities === null
    };
};

export default useQuickActions;
